import React, { useEffect, useState, useRef } from 'react';
import CustomHeader from '../../components/header/index.jsx';
import socketIOClient from 'socket.io-client';
import './policyupgradeagent.scss';
import { Container, Form } from 'react-bootstrap';
import url from '../../config.js';
import {
    Play,
    RefreshCw,
    AlertCircle,
    Clock,
    ChevronDown,
    ChevronRight,
    Loader,
    CheckCircle,
    XCircle
} from 'lucide-react';

const domain = url.domain;
const backendUrl = url.backendUrl;

const socketEndpoint = url.SOCKET;

function PolicyUpgradeAgent() {
    const [migrationStatus, setMigrationStatus] = useState('idle');
    const [setupStatus, setSetupStatus] = useState('idle');
    const [logs, setLogs] = useState([]);
    const [wsConnected, setWsConnected] = useState(false);
    const [summary, setSummary] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadedFilePath, setUploadedFilePath] = useState('');    // Configuration state
    const [showConfig, setShowConfig] = useState(true);
    const [manifestPath, setManifestPath] = useState('manifests/policy-manifest.yaml');
    const [dryRun, setDryRun] = useState(false);
    const [orgId, setOrgId] = useState('');
    const [envId, setEnvId] = useState('');
    const [groupId, setGroupId] = useState('');
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');

    const logsEndRef = useRef(null);
    const wsRef = useRef(null);

    useEffect(() => {
        connectWebSocket();
        loadSavedConfig();
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, []);

    useEffect(() => {
        if (logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs]);

    const connectWebSocket = () => {
        try {
            const socket = socketIOClient(socketEndpoint, { rejectUnauthorized: true, transports: ['websocket'] });

            socket.on("connect", function () {
                console.log("Socket connected");
                setWsConnected(true);
                addLog('success', 'Connected to policy upgrade server');
            });

            socket.on("policy-upgradation", (data) => {
                handleWebSocketMessage(data);
            });

            socket.on("disconnect", function () {
                setWsConnected(false);
                addLog('warning', 'Disconnected from server');
            });

            wsRef.current = socket;
        } catch (error) {
            console.error('Failed to create WebSocket connection:', error);
            setWsConnected(false);
        }
    };

   const handleWebSocketMessage = (message) => {
    console.log('📨 WebSocket message received:', message);

    switch (message.type) {
        case 'log':
            addLog(message.data.type, message.data.message);
            break;
        case 'summary':
            setSummary(message.data);
            break;
        case 'complete':
            // Set final status
            setSetupStatus(message.data.success ? 'completed' : 'failed');
            
            // Update summary if provided
            if (message.data.summary) {
                setSummary(message.data.summary);
            }
           
            if (!message.data.success && message.data.details) {
                console.error('Error details:', message.data.details);
            }
            break;
        default:
            console.log('Unknown message type:', message.type);
            break;
    }
};

   
    const addLog = (type, message) => {
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, { type, message, timestamp }]);
    };

    const loadSavedConfig = () => {
        const saved = localStorage.getItem('policyManagerConfig');
        if (saved) {
            try {
                const config = JSON.parse(saved);
                setManifestPath(config.manifestPath || 'manifests/policy-manifest.yaml');
                setOrgId(config.orgId || '');
                setEnvId(config.envId || '');
                setGroupId(config.groupId || '');
                setClientId(config.clientId || '');
            } catch (e) {
                console.error('Failed to load saved config:', e);
            }
        }
    };

    const saveConfig = () => {
        const config = {
            manifestPath,
            orgId,
            envId,
            groupId,
            clientId
        };
        localStorage.setItem('policyManagerConfig', JSON.stringify(config));
        addLog('success', '✓ Configuration saved locally');
    };

    const isConfigValid = () => {
        return manifestPath.trim() &&
            orgId.trim() &&
            envId.trim() &&
            groupId.trim() &&
            clientId.trim() &&
            clientSecret.trim();
    };

    const validateConfig = () => {
        if (!isConfigValid()) {
            addLog('error', 'Please complete all configuration fields');
            return false;
        }
        return true;
    };
    // const handleFileUpload = async (event) => {
    //     const file = event.target.files[0];
    //     if (!file) return;

    //     setSelectedFile(file);
    //     addLog('info', `Uploading file: ${file.name}...`);

    //     try {
    //         const formData = new FormData();
    //         formData.append('files', file);

    //          const response = await fetch(backendUrl + '/api/v1/upload-project', {
    //             method: 'POST',
    //             body: formData,
    //         });


    //         const result = await response.json();

    //         if (result.success) {
    //             const serverPath = result.filePath; // e.g., "manifests/policy-manifest-123456.yaml"
    //             setUploadedFilePath(serverPath);
    //             setManifestPath(serverPath);
    //             addLog('success', `✓ File uploaded: ${serverPath}`);
    //         } else {
    //             addLog('error', 'Failed to upload file');
    //             setSelectedFile(null);
    //         }
    //     } catch (error) {
    //         addLog('error', `Upload failed: ${error.message}`);
    //         setSelectedFile(null);
    //     }
    // };
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setSelectedFile(file);
        addLog('info', `Uploading file: ${file.name}...`);

        try {
            const formData = new FormData();
            formData.append('files', file);

            // Add filePaths as JSON (required by your uploadProject function)
            const filePaths = [file.name];
            formData.append('filePaths', JSON.stringify(filePaths));

            const response = await fetch(backendUrl + '/api/v1/upload-project', {  // Use your existing endpoint
                method: 'POST',
                headers: {
                    'x-access-token': localStorage.getItem('token')
                },
                body: formData
            });

            const result = await response.json();

            if (result.success) {
                // Construct the manifest path from projectId
                const serverPath = `${result.projectId}/${file.name}`;
                setUploadedFilePath(serverPath);
                setManifestPath(serverPath);
                addLog('success', `✓ File uploaded: ${serverPath}`);
            } else {
                addLog('error', result.error || 'Failed to upload file');
                setSelectedFile(null);
            }
        } catch (error) {
            addLog('error', `Upload failed: ${error.message}`);
            setSelectedFile(null);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'running':
                return <Loader className="spinning" />;
            case 'completed':
                return <CheckCircle />;
            case 'failed':
                return <XCircle />;
            default:
                return <Play />;
        }
    };

    const startUpgrade = async () => {
        console.log('Starting policy upgrade...');



        if (!wsConnected) {
            addLog('error', 'Not connected to server');
            return;
        }

        setMigrationStatus('running');
        setLogs([]);
        setSummary(null);

        try {
            const response = await fetch(domain + '/api/v1/policyupgrade', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': localStorage.getItem('token')
                },
                body: JSON.stringify({
                    manifestPath: manifestPath.trim(),
                    dryRun,
                    orgId: orgId.trim(),
                    envId: envId.trim(),
                    groupId: groupId.trim(),
                    clientId: clientId.trim(),
                    clientSecret: clientSecret.trim()
                })
            });

            const result = await response.json();
            console.log('📦 Upgrade API response:', result);

            if (result.success) {
                setSessionId(result.sessionId);
                addLog('success', 'Upgrade started successfully');
            } else {
                addLog('error', result.message || 'Failed to start upgrade');
                setMigrationStatus('idle');
            }
        } catch (error) {
            console.error('❌ Upgrade error:', error);
            addLog('error', `Failed: ${error.message}`);
            setMigrationStatus('idle');
        }
    };

    const startSetup = async () => {
        console.log('Starting upgrade setup...');

        if (!validateConfig()) {
            return;
        }

        if (!wsConnected) {
            addLog('error', 'Not connected to server');
            return;
        }

        setSetupStatus('running');
        setLogs([]);
        setSummary(null);

        try {
            const response = await fetch(domain + '/api/v1/upgradesetup', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': localStorage.getItem('token')
                },
                body: JSON.stringify({
                    manifestPath: manifestPath.trim(),
                    dryRun,
                    orgId: orgId.trim(),
                    envId: envId.trim(),
                    groupId: groupId.trim(),
                    clientId: clientId.trim(),
                    clientSecret: clientSecret.trim()
                })
            });

            const result = await response.json();
            console.log('📦 Setup API response:', result);

            if (result.success) {
                setSessionId(result.sessionId);
                addLog('success', 'Setup completed successfully');
                setSetupStatus('idle');
            } else {
                addLog('error', result.message || 'Failed to start setup');
                setSetupStatus('idle');
            }
        } catch (error) {
            console.error('❌ Setup error:', error);
            addLog('error', `Failed: ${error.message}`);
            setSetupStatus('idle');
        }
    };

    const clearLogs = () => {
        setLogs([]);
        setSummary(null);
    };

    return (
        <div className="root">
            <CustomHeader />
            <main className="content">
                <div className="project-overview-wrap">
                    <Container className="project-overview-container">
                        <div className="mule-migration">
                            <div className="mule-container">
                                <h1 className="mule-page-title">Mule Policy Upgrade Agent</h1>

                                <div className="mule-layout">
                                    {/* Left Panel - Configuration & Control Buttons */}
                                    <aside className="mule-sidebar">

                                        {/* Configuration Panel */}
                                        <div style={{
                                            backgroundColor: '#2c3e50',
                                            borderRadius: '8px',
                                            marginBottom: '20px',
                                            overflow: 'hidden'
                                        }}>
                                            {/* Configuration Header */}
                                            <div
                                                onClick={() => setShowConfig(!showConfig)}
                                                style={{
                                                    padding: '12px 15px',
                                                    backgroundColor: '#34495e',
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                <span style={{ fontWeight: '600', fontSize: '14px' }}>
                                                    Configuration {isConfigValid() ? '✓' : '⚠'}
                                                </span>
                                                {showConfig ?
                                                    <ChevronDown size={18} /> :
                                                    <ChevronRight size={18} />
                                                }
                                            </div>

                                            {/* Configuration Form */}
                                            {showConfig && (
                                                <div style={{
                                                    padding: '15px',
                                                    maxHeight: '400px',
                                                    overflowY: 'auto'
                                                }}>
                                                    <Form>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label style={{ color: '#ecf0f1', fontSize: '12px', marginBottom: '4px' }}>
                                                                Manifest File *
                                                            </Form.Label>
                                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="text"
                                                                    placeholder="manifests/policy-manifest.yaml"
                                                                    value={manifestPath}
                                                                    onChange={(e) => setManifestPath(e.target.value)}
                                                                    disabled={migrationStatus === 'running' || setupStatus === 'running'}
                                                                    style={{ flex: 1 }}
                                                                />
                                                                <label
                                                                    htmlFor="file-upload"
                                                                    style={{
                                                                        padding: '5px 10px',
                                                                        fontSize: '11px',
                                                                        backgroundColor: '#3498db',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer',
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    📄 Upload
                                                                </label>
                                                                <input
                                                                    id="file-upload"
                                                                    type="file"
                                                                    accept=".yaml,.yml,.csv"
                                                                    onChange={handleFileUpload}
                                                                    style={{ display: 'none' }}
                                                                    disabled={migrationStatus === 'running' || setupStatus === 'running'}
                                                                />
                                                            </div>
                                                            {uploadedFilePath && (
                                                                <Form.Text style={{ color: '#27ae60', fontSize: '10px' }}>
                                                                    ✓ Uploaded: {uploadedFilePath}
                                                                </Form.Text>
                                                            )}
                                                        </Form.Group>

                                                        <Form.Group className="mb-2">
                                                            <Form.Label style={{ color: '#ecf0f1', fontSize: '12px', marginBottom: '4px' }}>
                                                                Organization ID *
                                                            </Form.Label>
                                                            <Form.Control
                                                                size="sm"
                                                                type="text"
                                                                placeholder="Enter Organization ID"
                                                                value={orgId}
                                                                onChange={(e) => setOrgId(e.target.value)}
                                                                disabled={migrationStatus === 'running' || setupStatus === 'running'}
                                                            />
                                                        </Form.Group>

                                                        <Form.Group className="mb-2">
                                                            <Form.Label style={{ color: '#ecf0f1', fontSize: '12px', marginBottom: '4px' }}>
                                                                Environment ID *
                                                            </Form.Label>
                                                            <Form.Control
                                                                size="sm"
                                                                type="text"
                                                                placeholder="Enter Environment ID"
                                                                value={envId}
                                                                onChange={(e) => setEnvId(e.target.value)}
                                                                disabled={migrationStatus === 'running' || setupStatus === 'running'}
                                                            />
                                                        </Form.Group>

                                                        <Form.Group className="mb-2">
                                                            <Form.Label style={{ color: '#ecf0f1', fontSize: '12px', marginBottom: '4px' }}>
                                                                Group ID (Exchange Group) *
                                                            </Form.Label>
                                                            <Form.Control
                                                                size="sm"
                                                                type="text"
                                                                placeholder="Enter Group ID"
                                                                value={groupId}
                                                                onChange={(e) => setGroupId(e.target.value)}
                                                                disabled={migrationStatus === 'running' || setupStatus === 'running'}
                                                            />
                                                        </Form.Group>

                                                        <Form.Group className="mb-2">
                                                            <Form.Label style={{ color: '#ecf0f1', fontSize: '12px', marginBottom: '4px' }}>
                                                                Client ID *
                                                            </Form.Label>
                                                            <Form.Control
                                                                size="sm"
                                                                type="text"
                                                                placeholder="Enter Client ID"
                                                                value={clientId}
                                                                onChange={(e) => setClientId(e.target.value)}
                                                                disabled={migrationStatus === 'running' || setupStatus === 'running'}
                                                            />
                                                        </Form.Group>

                                                        <Form.Group className="mb-2">
                                                            <Form.Label style={{ color: '#ecf0f1', fontSize: '12px', marginBottom: '4px' }}>
                                                                Client Secret *
                                                            </Form.Label>
                                                            <Form.Control
                                                                size="sm"
                                                                type="password"
                                                                placeholder="Enter Client Secret"
                                                                value={clientSecret}
                                                                onChange={(e) => setClientSecret(e.target.value)}
                                                                disabled={migrationStatus === 'running' || setupStatus === 'running'}
                                                            />
                                                            <Form.Text style={{ color: '#95a5a6', fontSize: '10px' }}>
                                                                Not saved locally for security
                                                            </Form.Text>
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Check
                                                                type="checkbox"
                                                                label="Dry Run (test mode)"
                                                                checked={dryRun}
                                                                onChange={(e) => setDryRun(e.target.checked)}
                                                                disabled={migrationStatus === 'running' || setupStatus === 'running'}
                                                                style={{ color: '#ecf0f1', fontSize: '12px' }}
                                                            />
                                                        </Form.Group>

                                                        <button
                                                            type="button"
                                                            onClick={saveConfig}
                                                            disabled={!isConfigValid()}
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px',
                                                                fontSize: '12px',
                                                                backgroundColor: isConfigValid() ? '#27ae60' : '#95a5a6',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: isConfigValid() ? 'pointer' : 'not-allowed',
                                                                fontWeight: '600'
                                                            }}
                                                        >
                                                            💾 Save Configuration
                                                        </button>
                                                    </Form>
                                                </div>
                                            )}
                                        </div>

                                        {/* Action Buttons */}
                                        <button
                                            onClick={startSetup}
                                            disabled={setupStatus === 'running' || !wsConnected || !isConfigValid()}
                                            className={`mule-btn-start ${setupStatus !== 'running' && wsConnected && isConfigValid()
                                                ? 'mule-btn-start--enabled'
                                                : 'mule-btn-start--disabled'
                                                }`}
                                        >
                                            {getStatusIcon(setupStatus)}
                                            <span className="mule-btn-start__text">
                                                {setupStatus === 'running' ? 'Setup in Progress...' :
                                                    setupStatus === 'completed' ? 'Setup Complete' :
                                                        'Start Upgrade Setup'}
                                            </span>
                                        </button>

                                        <button
                                            onClick={startUpgrade}
                                            disabled={migrationStatus === 'running'}
                                            className={`mule-btn-start ${migrationStatus !== 'running' && wsConnected
                                                ? 'mule-btn-start--enabled'
                                                : 'mule-btn-start--disabled'
                                                }`}
                                        >
                                            {getStatusIcon(migrationStatus)}
                                            <span className="mule-btn-start__text">
                                                {migrationStatus === 'running' ? 'Upgrade in Progress...' :
                                                    migrationStatus === 'completed' ? 'Upgrade Complete' :
                                                        'Start Upgrade'}
                                            </span>
                                        </button>



                                        {/* Summary Cards */}
                                        {/* {summary && (
                                            <section className="mule-card" style={{ marginTop: '20px' }}>
                                                <h2 className="mule-card__title">Summary</h2>
                                                <div className="mule-summary-grid">
                                                    <div className="mule-summary-card">
                                                        <div className="mule-summary-card__label">Total</div>
                                                        <div className="mule-summary-card__value">{summary.total}</div>
                                                    </div>
                                                    <div className="mule-summary-card mule-summary-card--success">
                                                        <div className="mule-summary-card__label">Success</div>
                                                        <div className="mule-summary-card__value">{summary.success}</div>
                                                    </div>
                                                    <div className="mule-summary-card mule-summary-card--failed">
                                                        <div className="mule-summary-card__label">Failed</div>
                                                        <div className="mule-summary-card__value">{summary.failed}</div>
                                                    </div>
                                                    <div className="mule-summary-card">
                                                        <div className="mule-summary-card__label">Duration</div>
                                                        <div className="mule-summary-card__value">{summary.duration}s</div>
                                                    </div>
                                                </div>
                                            </section>
                                        )} */}
                                    </aside>

                                    {/* Right Panel - Logs */}
                                    <main className="mule-main-content">
                                        <section className="mule-card mule-logs-card">
                                            <aside>
                                                <div className="mule-card__header">
                                                    <h2 className="mule-card__title">
                                                        <AlertCircle className="mule-card__icon" />
                                                        Upgrade Logs
                                                    </h2>
                                                    <button onClick={clearLogs} className="mule-btn-clear-logs">
                                                        <RefreshCw className="mule-btn-clear-logs__icon" />
                                                    </button>
                                                </div>

                                                {/* Status Badge */}
                                                {(migrationStatus === 'running' || setupStatus === 'running') && (
                                                    <div className="migration-status migration-status--running">
                                                        <div className="migration-status__content">
                                                            <Clock className="migration-status__icon spinning" />
                                                            <span>
                                                                {setupStatus === 'running' ? 'Setup in progress...' : 'Upgrade in progress...'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="mule-logs-container" style={{
                                                    maxHeight: 'calc(100vh - 350px)', overflowY: 'auto', minHeight: '400px'
                                                }}>
                                                    {logs.length === 0 ? (
                                                        <div className="mule-logs-empty">
                                                            Logs will appear here when upgrade starts...
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {logs.map((log, idx) => (
                                                                <div key={idx} className={`log-entry log-entry--${log.type}`}>
                                                                    <span className="log-entry__timestamp">[{log.timestamp}]</span>
                                                                    <span className="log-entry__message">{log.message}</span>
                                                                </div>
                                                            ))}
                                                            <div ref={logsEndRef} />
                                                        </>
                                                    )}
                                                </div>
                                            </aside>
                                        </section>
                                    </main>
                                </div>
                            </div>
                        </div>
                    </Container>
                </div>
            </main>
        </div>
    );
}

export default PolicyUpgradeAgent;